/**
 * Format Components
 *
 * Formats IBody[] and IBodySettings back into AiComponentInput[]
 * This is the reverse operation of parseAiComponents.
 */

import type { IBody, IBodySettings } from '@makyoapp/makyo-sdk/models/AppBuilder'

import type { AiComponentInput } from './registry'
import { COMPONENT_FORMAT_REGISTRY, SPECIAL_COMPONENT_FORMATTERS } from './registry'

/**
 * Format a single component from IBody + settings to AiComponentInput
 */
function formatComponent(
  body: IBody,
  settings: IBodySettings
): AiComponentInput {
  const componentSettings = settings[body._id] || {}
  const componentType = body._ComponentID

  // Handle special components (tabs, form) that have custom structure
  if (componentType in SPECIAL_COMPONENT_FORMATTERS) {
    return SPECIAL_COMPONENT_FORMATTERS[componentType](body, settings, formatComponent)
  }

  // Get the component formatter from registry
  const formatter = COMPONENT_FORMAT_REGISTRY[componentType]
  if (!formatter) {
    throw new Error(`No formatter found for component type: ${componentType}. Format function not yet implemented.`)
  }

  // Call format function
  const formatted = formatter(componentSettings)
  
  // Extract type from formatted (or use componentType as fallback)
  const componentTypeValue = (formatted as any).type || componentType
  
  // Build input object with type and id first, then other properties
  // Destructure to exclude type from formatted since we set it explicitly first
  const { type: _, ...restFormatted } = formatted as any
  
  // Create ordered object: type first, then id, then all other properties
  // Using object literal with explicit property order ensures JSON.stringify preserves order
  const input: AiComponentInput = {
    type: componentTypeValue,
    ...(body._id ? { id: body._id } : {}),
    ...restFormatted,
  } as AiComponentInput

  // Add base properties (title comes after type and id)
  if (body._Title) {
    input.title = body._Title
  }

  // Copy actions as-is (no conversion needed)
  // Actions are stored in settings (e.g., 'component-action-click') and copied directly
  // They remain in their original BuilderActions structure
  // Note: For now, we skip actions as per user request - they stay in the original structure

  // Recursively format children
  if (body._Body && body._Body.length > 0) {
    input.children = body._Body.map((child) => formatComponent(child, settings))
  }

  return input
}

/**
 * Format components from IBody[] and IBodySettings to AiComponentInput[]
 */
export function formatComponents(bodies: IBody[], settings: IBodySettings): AiComponentInput[] {
  return bodies.map((body) => formatComponent(body, settings))
}

